<script setup lang="ts">
import type { Notification, NotifyHandler, NotifyTypes } from '@/composables/notifications';
import { useResizeObserver } from '@vueuse/core';
import { onMounted, ref, watch } from 'vue';
import { Sonner } from '@/components/base/sonner';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{ legacyNotifications: Notification[] }>();

const emit = defineEmits<{ 'clear:legacyNotifications': [] }>();

const { processSessionNotifications, notify } = useNotifications();

const options = ref({
    offset: {
        top: 'var(--notifications-offset-top)',
    },

    mobileOffset: {
        top: 'var(--notifications-offset-top)',
    },

    style: {
        '--width': 'auto',
        left: '50%',
        transform: '',
    },

    toastOptions: {
        duration: 6_000,
        style: {
            width: 'max-content',
            minWidth: '35vw',
            maxWidth: '75vw',
            transform: 'translateX(-50%) var(--y)',
            padding: 'var(--notifications-page-padding-y) var(--notifications-page-padding-x)',
        },
    },

    position: 'top-left' as const,
});

function updateOffset(el: Element) {
    if (!el) {
        return;
    }

    const rect = el.getBoundingClientRect();

    if (rect.width >= 600) {
        options.value.style['--width'] = `${rect.width}px`;
        options.value.style.left = `calc(${rect.left}px + ${rect.width / 2}px)`;
        options.value.toastOptions.style.minWidth = `${rect.width / 3}px`;
        options.value.toastOptions.style.maxWidth = `${rect.width}px`;
        options.value.toastOptions.style.width = 'max-content';
    } else {
        options.value.style['--width'] = `${rect.width}px`;
        options.value.style.left = '50%';
        options.value.toastOptions.style.minWidth = '35vw';
        options.value.toastOptions.style.maxWidth = 'calc(100vw - var(--notifications-page-padding) * 2)';
        options.value.toastOptions.style.width = `calc(${rect.width}px - var(--notifications-page-padding) * 4)`;
    }
}

onMounted(() => {
    const mainEl
        = document.querySelector('.kgr-pageLayout__mainContent')
            || document.querySelector('.kgr-pageLayout__main') || document.body;

    if (mainEl) {
        useResizeObserver(mainEl as any, () => updateOffset(mainEl));
        updateOffset(mainEl);
    }

    processSessionNotifications();

    // handle legacy notifications
    watch(props.legacyNotifications, (list) => {
        list.forEach(({ message, params, settings }: Notification) => {
            const type: NotifyTypes = settings?.type || 'info';
            (notify as unknown as Record<NotifyTypes, NotifyHandler>)[type](message, params, settings);
        });

        emit('clear:legacyNotifications');
    }, { deep: true, immediate: true });
});
</script>

<template>
    <Sonner
        v-bind="options"
    />
</template>

<style>
:root {
    --notifications-offset-top: calc(var(--kgr-pageLayout-header-height) + var(--kgr-region-padding) * 1.5);
    --notifications-page-padding: var(--kgr-region-padding);
    --notifications-page-padding-x: 1rem;
    --notifications-page-padding-y: 1rem;
}
</style>
